import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import EventEHRConfirmationDialog from "../../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import { Close as CloseIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import moment from "moment";
import { getAllOrderNotes, createOrderNote, updateOrderNote, deleteOrderNote } from "../../patientSaga";
import { getLoginUserData } from "../../../../utils/getToken";

interface ViewNotesModalProps {
  isOpen: boolean;
  handleClose: () => void;
  orderData: any;
}

interface Note {
  id: string;
  note: string;
  createdAt: string;
  createdBy: {
    firstName: string;
    lastName: string;
  };
}

const ViewNotesModal: React.FC<ViewNotesModalProps> = ({
  isOpen,
  handleClose,
  orderData,
}) => {
  const dispatch = useDispatch();
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  // Get logged-in user data
  const parseLoginUserdata: any = getLoginUserData();
  const loginUserdata = JSON.parse(parseLoginUserdata);

  // Fetch notes when modal opens
  useEffect(() => {
    if (isOpen && orderData?.id) {
      fetchOrderNotes();
    }
  }, [isOpen, orderData?.id]);

  const fetchOrderNotes = () => {
    setIsLoading(true);
    dispatch(
      getAllOrderNotes({
        orderId: orderData?.id,
        onSuccess: (data: any) => {
          setNotes(data?.note || []);
          setIsLoading(false);
        },
        onError: (error: any) => {
          setNotes([]);
          setIsLoading(false);
        },
      })
    );
  };

  const handleAddNote = () => {
    if (newNote?.trim() && orderData?.id) {
      dispatch(
        createOrderNote({
          orderId: orderData?.id,
          noteData: { note: newNote?.trim() },
          onSuccess: (data: any) => {
            // Add the new note to the list - use the actual ID from server response
            if (data?.id) {
              const newNoteObj: Note = {
                id: data.id,
                note: newNote?.trim(),
                createdAt: data?.createdAt || new Date().toISOString(),
                createdBy: data?.createdBy || { 
                  firstName: loginUserdata?.name?.split(' ')[0] || "Current", 
                  lastName: loginUserdata?.name?.split(' ').slice(1).join(' ') || "User" 
                },
              };
              setNotes(prev => [newNoteObj, ...prev]);
              setNewNote("");
              setIsAddingNote(false);
            } else {
              // If no ID returned, refetch notes to get the complete data
              fetchOrderNotes();
              setNewNote("");
              setIsAddingNote(false);
            }
          },
          onError: () => {
            // Handle error if needed
          },
        })
      );
    }
  };

  const handleCancelAdd = () => {
    setNewNote("");
    setIsAddingNote(false);
  };

  const handleEditNote = (note: Note) => {
    setEditingNoteId(note.id);
    setEditNoteText(note.note);
  };

  const handleUpdateNote = () => {
    if (editNoteText?.trim() && editingNoteId) {
      dispatch(
        updateOrderNote({
          noteId: editingNoteId,
          noteData: { note: editNoteText?.trim() },
          onSuccess: (data: any) => {
            setNotes(prev => 
              prev?.map(note => 
                note?.id === editingNoteId 
                  ? { ...note, note: editNoteText?.trim() }
                  : note
              )
            );
            setEditingNoteId(null);
            setEditNoteText("");
          },
          onError: () => {
            // Handle error if needed
          },
        })
      );
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNoteText("");
  };

  const handleDeleteNote = (noteId: string) => {
    setNoteToDelete(noteId);
    setOpenDeleteConfirmDialog(true);
  };

  const confirmDeleteNote = () => {
    if (noteToDelete) {
      dispatch(
        deleteOrderNote({
          noteId: noteToDelete,
          onSuccess: () => {
            setNotes(prev => prev?.filter(note => note?.id !== noteToDelete));
            setOpenDeleteConfirmDialog(false);
            setNoteToDelete(null);
          },
          onError: () => {
            // Handle error if needed
            setOpenDeleteConfirmDialog(false);
            setNoteToDelete(null);
          },
        })
      );
    }
  };

  const cancelDeleteNote = () => {
    setOpenDeleteConfirmDialog(false);
    setNoteToDelete(null);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          minHeight: "500px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 24px 16px",
          borderBottom: "1px solid #E0E0E0",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1D1D1D" }}>
          {/* View Notes - {orderData?.orderType || "Order"} */}
          View Notes 
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "0", display: "flex", flexDirection: "column", height: "500px" }}>
        {/* Fixed Order Information */}
        <Box sx={{ padding: "24px 24px 0 24px", flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: "16px" }}>
            Order Details
          </Typography>
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: "1fr 1fr", 
            gap: "16px",
            backgroundColor: "#f8f9fa",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #E0E0E0"
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Order Type
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, marginTop: "4px" }}>
                {orderData?.orderType || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Item
              </Typography>
              {orderData?.allItems && orderData?.allItems?.length > 1 ? (
                <Tooltip 
                  title={orderData?.allItems?.map((item: any) => item?.medicationName)?.join(', ')}
                  arrow
                  placement="top"
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, marginTop: "4px", cursor: "pointer" }}>
                    {orderData?.allItems?.[0]?.medicationName} (+{orderData?.allItems?.length - 1} more)
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, marginTop: "4px" }}>
                  {orderData?.medicationName || "-"}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Order Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, marginTop: "4px" }}>
                {orderData?.orderDate || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Ordered By
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, marginTop: "4px" }}>
                {orderData?.orderedBy || "-"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ margin: "24px 0" }} />

        {/* Scrollable Notes Section */}
        <Box sx={{ 
          flex: 1, 
          overflow: "hidden", 
          display: "flex", 
          flexDirection: "column",
          padding: "0 24px"
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Notes ({notes?.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setIsAddingNote(true)}
              size="small"
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                borderColor: "#1976d2",
                color: "#1976d2",
                "&:hover": {
                  borderColor: "#1565c0",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Add Note
            </Button>
          </Box>

          {/* Add Note Form */}
          {isAddingNote && (
            <Box sx={{ marginBottom: "16px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter note description..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                variant="outlined"
                sx={{
                  marginBottom: "12px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelAdd}
                  size="small"
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!newNote?.trim()}
                  size="small"
                  sx={{ textTransform: "none" }}
                >
                  Add Note
                </Button>
              </Box>
            </Box>
          )}

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ 
              flex: 1, 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center",
              padding: "20px" 
            }}>
              <Typography variant="body2" color="text.secondary">
                Loading notes...
              </Typography>
            </Box>
          )}

          {/* Notes List */}
          {!isLoading && notes?.length > 0 && (
            <Box sx={{ 
              flex: 1, 
              overflow: "auto", 
              display: "flex", 
              flexDirection: "column", 
              gap: "12px",
              paddingRight: "8px",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
            }}>
              {notes?.map((note, index) => (
                <Box
                  key={note.id || index}
                  sx={{
                    padding: "16px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  {editingNoteId === note.id ? (
                    // Edit Mode
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={editNoteText}
                        onChange={(e) => setEditNoteText(e.target.value)}
                        variant="outlined"
                        sx={{
                          marginBottom: "12px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                          },
                        }}
                      />
                      <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                        <Button
                          variant="outlined"
                          onClick={handleCancelEdit}
                          size="small"
                          sx={{ textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleUpdateNote}
                          disabled={!editNoteText?.trim()}
                          size="small"
                          sx={{ textTransform: "none" }}
                        >
                          Update
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    // View Mode
                    <Box sx={{ position: "relative" }}>
                      {/* Action Icons - Top Right Corner */}
                      <Box sx={{ 
                        position: "absolute", 
                        top: "-8px", 
                        right: "-8px", 
                        display: "flex", 
                        gap: "4px",
                        backgroundColor: "#ffffff",
                        borderRadius: "4px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        padding: "2px"
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditNote(note)}
                          sx={{ 
                            padding: "4px",
                            "&:hover": { backgroundColor: "#f5f5f5" }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteNote(note.id)}
                          sx={{ 
                            padding: "4px",
                            "&:hover": { backgroundColor: "#ffebee" }
                          }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                      
                      <Typography variant="body2" sx={{ marginBottom: "8px", lineHeight: 1.5, paddingRight: "60px", fontWeight: 500 }}>
                        {note.note}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                          Added by: {note.createdBy ? `${note.createdBy.firstName} ${note.createdBy.lastName}` : "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {moment(note.createdAt).format("MM/DD/YYYY HH:mm")}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Empty State */}
          {!isLoading && notes?.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px dashed #E0E0E0",
                marginBottom: "24px",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                No notes available for this order.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", marginTop: "4px" }}>
                Click "Add Note" to add the first note.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px", borderTop: "1px solid #E0E0E0" }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            width: "120px",
            textTransform: "none",
            borderRadius: "6px",
            padding: "8px 16px",
            backgroundColor: "#005596",
            color: "#FFFFFF",
            border: "1px solid #005596",
            "&:hover": {
              backgroundColor: "#005596",
              color: "#FFFFFF",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
      
      {/* Delete Confirmation Dialog */}
      <EventEHRConfirmationDialog
        alertTitle="Delete Note"
        alertDescription="Are you sure you want to delete this note? This action cannot be undone."
        cancelButtonTitle="Cancel"
        submitButtonTitle="Delete"
        open={openDeleteConfirmDialog}
        handleClose={(action: string) => {
          if (action === "close" || action === "cancel") {
            cancelDeleteNote();
          } else if (action === "submit") {
            confirmDeleteNote();
          }
        }}
      />
    </Dialog>
  );
};

export default ViewNotesModal;
